/**
 * Pure helpers behind the "Trackable Shareable Links" panel in the Associate
 * Tools card (client/src/pages/RepDashboard.tsx).
 *
 * The panel lists one trackable link per vertical the logged-in associate can
 * sell. Each URL is the admin-configured landing page for that vertical with
 * the associate's OWN repCode attached by withRefCode() - the same builder the
 * per-vertical Referral Links tab uses, so attribution behaves identically on
 * both screens (and a base URL with a stale ?ref= baked in cannot shadow the
 * real associate's code).
 *
 * This lives here rather than inline in the page so the part that decides
 * WHICH link belongs to WHOM is unit-testable. Getting it wrong means an
 * associate shares a link that credits someone else.
 */

import { withRefCode } from "@/lib/referralCookie";

export type ShareableMainLink = {
  /** The vertical whose row renders this link (the first one, when merged). */
  vertical: string;
  /** Display label. Merged rows collapse to the words their names share. */
  label: string;
  /** Full trackable URL, already carrying this associate's ?ref=. */
  url: string;
};

/**
 * Two verticals can legitimately point at the same landing page (Share360
 * Business and Share360 Nonprofit both default to share360.wibiz.ai). Listing
 * one URL twice under two names reads as a bug and over-claims which offer the
 * link opens, so merged rows are labelled with the leading words all the
 * merged names share ("Share360 Business" + "Share360 Nonprofit" -> "Share360").
 * With no shared leading word, the first name is kept rather than inventing one.
 */
export function sharedLabelPrefix(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  const parts = names.map((n) => n.split(" "));
  const common: string[] = [];
  for (let i = 0; i < parts[0].length; i++) {
    const token = parts[0][i];
    if (parts.every((p) => p[i] === token)) common.push(token);
    else break;
  }
  return common.length > 0 ? common.join(" ") : names[0];
}

/**
 * Builds the main trackable link for each vertical, in the order given.
 *
 * - `adminUrls` is the admin-managed base URL per vertical (Admin > Referral
 *   Links > Main Referral Links). It wins when set.
 * - `defaultDomains` is the bare-host fallback for verticals an admin has not
 *   configured (VERTICAL_CONFIG's `domain`). An empty host yields no row, which
 *   is how BizConnect360 (no landing page of its own) drops out.
 * - A blank `repCode` yields NO rows at all. An untracked landing-page URL
 *   looks like it works while silently losing the associate their credit, so
 *   the caller shows an unavailable state instead.
 */
export function resolveMainShareableLinks(
  verticals: string[],
  adminUrls: Record<string, string | undefined>,
  defaultDomains: Record<string, string | undefined>,
  repCode: string | null | undefined,
): ShareableMainLink[] {
  if (!repCode || !repCode.trim()) return [];
  const byUrl = new Map<string, { vertical: string; owners: string[] }>();
  const order: string[] = [];
  for (const vertical of verticals) {
    const base = adminUrls[vertical] || (defaultDomains[vertical] ? `https://${defaultDomains[vertical]}/` : "");
    if (!base) continue;
    const url = withRefCode(base, repCode);
    const existing = byUrl.get(url);
    if (existing) {
      existing.owners.push(vertical);
      continue;
    }
    byUrl.set(url, { vertical, owners: [vertical] });
    order.push(url);
  }
  return order.map((url) => {
    const { vertical, owners } = byUrl.get(url)!;
    return { vertical, label: sharedLabelPrefix(owners), url };
  });
}
